import { useMemo } from "react"
import { Button } from "@/components/ui/button"
import type { DataImport, ImportField } from "@/hooks/use-data-imports"

interface StepConfirmProps {
  importData: DataImport | null
  fieldMapping: Record<string, string>
  availableFields: ImportField[]
  totalRows: number
  onExecute: () => void
  onBack: () => void
  isLoading: boolean
}

export function StepConfirm({
  importData,
  fieldMapping,
  availableFields,
  totalRows,
  onExecute,
  onBack,
  isLoading,
}: StepConfirmProps) {
  const entityLabel = importData?.entity_type === "companies" ? "Kontrahenci" : "Kontakty"

  const mappedFieldsList = useMemo(() => {
    return Object.entries(fieldMapping)
      .filter(([, targetField]) => targetField && targetField !== "_skip")
      .map(([csvColumn, targetField]) => {
        const field = availableFields.find((f) => f.key === targetField)
        return {
          csvColumn,
          targetLabel: field?.label || targetField,
          required: field?.required || false,
        }
      })
  }, [fieldMapping, availableFields])

  const skippedColumns = useMemo(() => {
    const allHeaders = importData?.csv_headers || []
    return allHeaders.filter(
      (h) => !fieldMapping[h] || fieldMapping[h] === "_skip"
    )
  }, [importData, fieldMapping])

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Plik</span>
          <span className="text-sm font-medium">{importData?.file_name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Typ danych</span>
          <span className="text-sm font-medium">{entityLabel}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Liczba wierszy</span>
          <span className="text-sm font-medium">{totalRows}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Zmapowane pola</span>
          <span className="text-sm font-medium">{mappedFieldsList.length}</span>
        </div>
        {skippedColumns.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Pominiete kolumny</span>
            <span className="text-sm font-medium text-muted-foreground">
              {skippedColumns.length}
            </span>
          </div>
        )}
      </div>

      {/* Field mapping summary */}
      <div>
        <h4 className="text-sm font-medium mb-2">Mapowanie pol</h4>
        <div className="rounded-lg border divide-y max-h-[200px] overflow-y-auto">
          {mappedFieldsList.map(({ csvColumn, targetLabel, required }) => (
            <div
              key={csvColumn}
              className="flex items-center justify-between px-3 py-2"
            >
              <span className="text-sm text-muted-foreground">{csvColumn}</span>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
                <span className="text-sm font-medium">
                  {targetLabel}
                  {required && <span className="text-destructive ml-1">*</span>}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {skippedColumns.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-1 text-muted-foreground">
            Pominiete kolumny
          </h4>
          <p className="text-xs text-muted-foreground">
            {skippedColumns.join(", ")}
          </p>
        </div>
      )}

      {/* Warning */}
      <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-3">
        <p className="text-sm text-amber-700 dark:text-amber-400">
          Import utworzy {totalRows} nowych rekordow typu &quot;{entityLabel}&quot;.
          Tej operacji nie mozna cofnac automatycznie.
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} disabled={isLoading}>
          Wstecz
        </Button>
        <Button onClick={onExecute} disabled={isLoading}>
          {isLoading ? "Rozpoczynanie..." : "Rozpocznij import"}
        </Button>
      </div>
    </div>
  )
}
